import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import RenderVideo from './PageDetails/RenderVideo';
import ConfirmDescription from './ConfirmDescription';
import { AlertCircle, Button, CheckCircle, DatePicker, Radio, RadioGroup, Ticket01 } from '@opendate-app/setlist';
import {
  BASIC_INFO_MENU_ITEM,
  TICKETS_MENU_ITEM,
} from '../constants/confirmTicketingConstants';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

import {
  offsetDateForBrowser,
} from '../../../shared/timeZoneLogic';

const renderablePageDetails  = (pageDetails) => {
  return pageDetails.filter((pd) => pd.renderable);
}

const ticketTypePrice = (ticketType) => {
  if(ticketType.free){
    return "FREE"
  } else {
    return formatter.format(parseFloat(ticketType.price));
  }
}

const ticketTypesSummary = (ticketTypes) => {
  var sorted = ticketTypes.sort((a, b) => {
    return parseFloat(a.price) - parseFloat(b.price)
  });

  if(sorted.length === 1){
    return <span>{ticketTypePrice(sorted[0])}</span>;
  } else {
    var firstTicketType = sorted[0];
    var lastTicketType = sorted[sorted.length - 1];

    return <span>{ticketTypePrice(firstTicketType)} - {ticketTypePrice(lastTicketType)}</span>;
  }
}

export const publishRequirements = (confirm, team) => [
  {
    label: "Publish date and time set",
    met: !!confirm.published_at,
  },
  {
    label: "Venue capacity entered",
    met: !!confirm.venue_capacity && parseInt(confirm.venue_capacity) >= 0,
    step: BASIC_INFO_MENU_ITEM,
  },
  {
    label: "Age restriction selected",
    met: !!confirm.age_restriction && confirm.age_restriction.length > 0,
    step: BASIC_INFO_MENU_ITEM,
  },
  {
    label: "General Admission, or a seat map assigned",
    met: confirm.ticketing_arrangement === "General Admission"
      || (confirm.ticketing_arrangement === "Seated" && !!confirm.seating_chart_id),
    step: TICKETS_MENU_ITEM,
  },
  team.requires_stripe_connect && {
    label: "Stripe Connect set up",
    met: !!team.stripe_connect_ready,
    href: "/teams/" + team.id + "/integrations",
  },
].filter(Boolean);

const STEP_LABELS = {
  [BASIC_INFO_MENU_ITEM]: "Basic Info",
  [TICKETS_MENU_ITEM]: "Tickets",
};

const Publish = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  ticketTypes,
  pageDetails,
  updateConfirm,
  publishConfirm,
  onStepSelected,
  rootUrl
}) => {
  const requirements = publishRequirements(confirm, team);
  const canPublish = requirements.every((requirement) => requirement.met);

  useEffect(() => {
    if(!confirm.published_at) {
      updateConfirm(Object.assign({}, confirm, {is_public: false}));
    }
  }, []);

  return (
    <div className="row">
      <div className="col-12">
        <h3 style={{"fontSize": "22px"}}>
          <strong>
            Publish Your Event
          </strong>
        </h3>
        <div className="row"
             style={{"marginTop": "40px"}}>
          <div className="col-12 col-xl-6 mb-4">
            {confirm.compressed_main_event_image_url ? (
              <img src={confirm.compressed_main_event_image_url}
                   width="100%" />
            ) : null}
          </div>
          <div className="col-12 col-xl-6 mb-4">
            {confirm.presenter && confirm.presenter.length > 0 ? (
              <div className="extra-small mb-1"
                   style={{
                     "fontWeight": "700",
                     "color": "var(--sl-text-tertiary)",
                     "letterSpacing": '1px',
                     "textTransform": "uppercase"
                   }}>
                {confirm.presenter}
              </div>
            ) : null}
            <div style={{"fontSize": "21px"}}>
              <strong>{confirm.title}</strong>
            </div>
            {confirm.support_artist_names.length > 0 ? (
              <div className="mb-1" style={{"color": "var(--sl-text-tertiary)"}}>
                with {confirm.support_artist_names}
              </div>
            ) : null}
            <p className="mb-1"
              style={{"fontSize": "16px", "color": "var(--sl-text-secondary)"}}>
              {confirm.age_restriction}
            </p>
            <div className="mb-1" style={{"color": "var(--sl-text-tertiary)"}}>
              {confirm.human_date_and_time_with_day_of_week}
            </div>
            {confirm.venue.address_from_components ? (
              <div className="small mb-1" style={{"color": "var(--sl-text-tertiary)"}}>
                {confirm.venue.address_from_components}
              </div>
            ) : null}
            {ticketTypes.length > 0 ? (
              <div className="small mb-1 d-flex align-items-center" style={{"color": "var(--sl-text-tertiary)"}}>
                <Ticket01 width={14} height={14} className="mr-2" />
                {ticketTypesSummary(ticketTypes)}
              </div>
            ) : null}
          </div>
        </div>
        {renderablePageDetails(pageDetails).length > 0 ? (
          <React.Fragment>
            <div className="row">
              <div className="col-12">
                <hr className="mb-4 mt-0"
                    style={{"borderTop": "1px solid var(--sl-border-secondary)"}} />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <ConfirmDescription pageDetails={pageDetails}
                                    rootUrl={rootUrl} />
              </div>
            </div>
          </React.Fragment>
        ) : null}
        <div className="row">
          <div className="col-12">
            <hr className="my-3"
                style={{"borderTop": "1px solid var(--sl-border-secondary)"}} />
            <h3 className="mb-3"
                style={{"fontSize": "20px"}}>
              <strong>
                Who can see your event?
              </strong>
            </h3>
            <RadioGroup name="confirm-visibility"
                        defaultValue={confirm.published_at ? (confirm.is_public ? "public" : "private") : undefined}
                        onChange={(visibility) => {
                          var updated = Object.assign({}, confirm, {
                            is_public: visibility === "public",
                            published_at: offsetDateForBrowser(new Date())
                          });

                          updateConfirm(updated);
                        }}>
              <Radio value="public" label="Public" />
              <p className="small"
                 style={{"marginBottom": "13px", "color": "var(--sl-text-tertiary)"}}>
                Shared across all networks, including Opendate and embedded.
              </p>
              {confirm.is_public ? (
                <div className="mb-3" style={{"width": "300px"}}>
                  <DatePicker time
                              aria-label="Publish date and time"
                              placeholder="Select date and time"
                              value={typeof confirm.published_at === 'string' ? offsetDateForBrowser(confirm.published_at) : (confirm.published_at || null)}
                              onChange={(publishedAt) => {
                                var updated = Object.assign({}, confirm, {published_at: publishedAt});
                                updateConfirm(updated);
                              }} />
                </div>
              ) : null}
              <Radio value="private" label="Private" />
              <p className="small" style={{"color": "var(--sl-text-tertiary)"}}>
                Only available to a select audience
              </p>
            </RadioGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex justify-content-end align-items-end"
               style={{"minHeight": "130px", "gap": "24px"}}>
            {canPublish ? null : (
              <ul className="list-unstyled mb-0" aria-label="Publish requirements">
                {requirements.map((requirement) => (
                  <li key={requirement.label}
                      className="d-flex align-items-center small mb-1"
                      style={{"color": requirement.met ? "var(--sl-text-tertiary)" : "var(--sl-text-primary)"}}>
                    {requirement.met ? (
                      <CheckCircle width={16} height={16} className="mr-2" style={{"color": "var(--sl-fg-success)"}} aria-label="Done" />
                    ) : (
                      <AlertCircle width={16} height={16} className="mr-2" style={{"color": "var(--sl-fg-warning)"}} aria-label="Missing" />
                    )}
                    {requirement.label}
                    {!requirement.met && requirement.step ? (
                      <Button variant="link-color" size="sm" className="ml-2" onClick={() => onStepSelected(requirement.step)}>
                        Go to {STEP_LABELS[requirement.step]}
                      </Button>
                    ) : !requirement.met && requirement.href ? (
                      <a className="ml-2" href={requirement.href}>Set up</a>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            <Button variant="primary"
                    disabled={!canPublish}
                    onClick={() => publishConfirm(csrfToken, team, confirm)}>
              Publish
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

Publish.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array,
  pageDetails: PropTypes.array,
  updateConfirm: PropTypes.func.isRequired,
  publishConfirm: PropTypes.func.isRequired,
  onStepSelected: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired
};

export default Publish;
